import React, { useState, useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, TextInput, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import Tooltip from '@/components/Tooltip';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useToastStore } from '@/store';
import { BORDER_RADIUS, MEMORY_CHAR_LIMIT } from '@/constants/config';
import copyText from '@/constants/utils/copy_text';
import { getMemoryThemeColors } from '@/components/Memory/memoryTheme';

const isWeb = Platform.OS === 'web';
const CHAR_COUNT_WARNING_COLOR = '#F59E0B';

function getCharCountProps(length, limit) {
  const ratio = length / limit;
  if (ratio > 0.9) return { color: 'danger', style: undefined };
  if (ratio > 0.75) return { color: undefined, style: { color: CHAR_COUNT_WARNING_COLOR } };
  return { color: 'secondary', style: undefined };
}

const getProvenanceConfig = (memColors) => ({
  dream: {
    labelKey: 'settings.memory.sourceDream',
    icon: 'sparkles',
    color: memColors.learned,
    bg: memColors.learnedBg,
    border: memColors.learnedBorder,
  },
  auto: {
    labelKey: 'settings.memory.sourceAuto',
    icon: 'bolt',
    color: memColors.auto,
    bg: memColors.autoBg,
    border: memColors.autoBorder,
  },
  explicit: {
    labelKey: 'settings.memory.sourceExplicit',
    icon: 'comments',
    color: memColors.explicit,
    bg: memColors.explicitBg,
    border: memColors.explicitBorder,
  },
  import: {
    labelKey: 'settings.memory.sourceImport',
    icon: 'file-import',
    color: memColors.import,
    bg: memColors.importBg,
    border: memColors.importBorder,
  },
  manual: {
    labelKey: 'pages.memory.filterManual',
    icon: 'user',
    color: memColors.manual,
    bg: memColors.manualBg,
    border: memColors.manualBorder,
  },
});

const createStyles = (colors, memColors) =>
  StyleSheet.create({
    memoryItem: {
      paddingVertical: 16,
      paddingHorizontal: 18,
      borderBottomWidth: 1,
      backgroundColor: memColors.surface,
      borderColor: memColors.border,
      ...(isWeb ? { transition: 'background-color 0.16s ease, border-color 0.16s ease' } : {}),
    },
    hoverContainer: {
      backgroundColor: memColors.hover,
    },
    memoryItemContent: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      minWidth: 0,
      width: '100%',
    },
    memoryText: {
      flex: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: 22,
      ...(isWeb ? { wordBreak: 'break-word', overflowWrap: 'anywhere' } : {}),
    },
    memoryActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
      ...(isWeb ? { transition: 'opacity 0.2s ease' } : { opacity: 1 }),
    },
    memoryActionsVisible: {
      opacity: 1,
    },
    actionButton: {
      padding: 8,
      minWidth: 36,
      minHeight: 36,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    actionButtonHovered: {
      backgroundColor: memColors.preview,
    },
    actionButtonDelete: {},
    actionButtonDeleteHovered: {
      backgroundColor: `${colors.color_destructive}14`,
    },
    editingItem: {
      borderColor: memColors.activeBorder,
      backgroundColor: memColors.selected,
    },
    editInputWrapper: {
      flex: 1,
    },
    editInput: {
      backgroundColor: memColors.control,
      borderRadius: BORDER_RADIUS.medium,
      padding: 12,
      width: '100%',
      borderWidth: 1,
      borderColor: memColors.border,
      color: colors.text_color_primary,
      fontSize: 14,
      lineHeight: 21,
      minHeight: 84,
      textAlignVertical: 'top',
      ...(isWeb ? { outlineStyle: 'none' } : {}),
    },
    editActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: 10,
      justifyContent: 'flex-end',
    },
    editMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 6,
    },
    memoryMeta: {
      marginTop: 8,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 10,
    },
    provenanceBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 6,
    },
    sourceLink: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 2,
      paddingHorizontal: 2,
      borderRadius: 8,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    sourceLinkHovered: {
      backgroundColor: memColors.preview,
    },
    checkboxContainer: {
      width: 20,
      height: 20,
      borderRadius: 6,
      borderWidth: 2,
      borderColor: memColors.activeBorder,
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 2,
      ...(isWeb ? { cursor: 'pointer', transition: 'all 0.16s ease' } : {}),
    },
    checkboxSelected: {
      backgroundColor: memColors.ink,
      borderColor: memColors.ink,
    },
    selectedItem: { backgroundColor: memColors.selected },
  });

const MemoryItem = ({
  memory,
  onUpdate,
  onDelete,
  formatDate,
  showCheckbox = false,
  isSelected = false,
  onSelect = null,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const router = useRouter();
  const { addToast } = useToastStore(state => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const memColors = useMemo(() => getMemoryThemeColors(theme, colors), [theme, colors]);
  const provenanceConfig = useMemo(() => getProvenanceConfig(memColors), [memColors]);

  const styles = useMemo(
    () => createStyles(colors, memColors),
    [colors, memColors]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState(memory.content);
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenSource = useCallback(() => {
    if (memory?.fromConversation) {
      const conversationId =
        typeof memory.fromConversation === 'object'
          ? memory.fromConversation?._id || memory.fromConversation?.id
          : memory.fromConversation;
      if (conversationId) {
        router.push(`/c/${conversationId}`);
      }
    }
  }, [memory?.fromConversation, router]);

  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
    setEditingContent(memory.content);
  }, [memory.content]);

  const handleCancelEditing = useCallback(() => {
    if (isSaving) return;
    setIsEditing(false);
    setEditingContent(memory.content);
  }, [memory.content, isSaving]);

  const handleUpdateMemory = useCallback(async () => {
    if (!editingContent.trim() || editingContent.length > MEMORY_CHAR_LIMIT || isSaving) return;
    setIsSaving(true);
    try {
      const ok = await onUpdate(memory.id, editingContent.trim());
      if (ok !== false) setIsEditing(false);
    } catch {
      // Keep the draft available for retry; callers report the request error.
    } finally {
      setIsSaving(false);
    }
  }, [editingContent, isSaving, onUpdate, memory.id]);

  const handleKeyDown = useCallback((e) => {
    if (!isWeb) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleUpdateMemory();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditing();
    }
  }, [handleUpdateMemory, handleCancelEditing]);

  const handleCopy = useCallback(async (e) => {
    e?.stopPropagation?.();
    try {
      await copyText(memory.content);
      setIsCopied(true);
      addToast(t('pages.memory.copied') || 'Copied to clipboard', 'success');
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      addToast(t('common.failedToCopy') || 'Failed to copy', 'error');
    }
  }, [memory.content, addToast, t]);

  const handleDeletePress = useCallback(() => {
    showConfirmationModal({
      title: t('settings.memory.deleteConfirmTitle'),
      message: t('settings.memory.deleteConfirmMessage'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      isDangerous: true,
      onConfirm: () => onDelete(memory.id),
    });
  }, [showConfirmationModal, t, onDelete, memory.id]);

  const handleHoverChange = (hovering) => {
    if (!isEditing && !isXS) {
      setIsHovered(hovering);
    }
  };

  const handleCheckboxPress = useCallback(() => {
    if (onSelect) {
      onSelect(memory.id);
    }
  }, [onSelect, memory.id]);

  // Provenance details
  const source = memory.source || (memory.isAutoGenerated ? 'auto' : 'manual');
  const provenance = provenanceConfig[source] || provenanceConfig.manual;
  const provenanceLabel = t(provenance.labelKey) || source;

  return (
    <Pressable
      style={[
        styles.memoryItem,
        isEditing && styles.editingItem,
        isHovered && !isEditing && styles.hoverContainer,
        isSelected && styles.selectedItem,
      ]}
      onHoverIn={() => handleHoverChange(true)}
      onHoverOut={() => handleHoverChange(false)}
      onPress={showCheckbox && !isEditing ? handleCheckboxPress : undefined}
      accessible={false}
      {...(isWeb ? { dataSet: { memoryItem: 'true' } } : null)}
    >
      {isEditing ? (
        <View style={styles.editInputWrapper}>
          <TextInput
            style={styles.editInput}
            editable={!isSaving}
            accessibilityLabel={t('settings.memory.editMemory')}
            value={editingContent}
            onChangeText={setEditingContent}
            multiline
            maxLength={MEMORY_CHAR_LIMIT + 50}
            autoFocus
            placeholder={t('settings.memory.editPlaceholder')}
            placeholderTextColor={colors.text_color_tertiary}
            onKeyPress={handleKeyDown}
          />
          <View style={styles.editMetaRow}>
            <Text size="xxs" color="tertiary">
              {isWeb ? t('pages.memory.saveShortcut') : ''}
            </Text>
            {(() => {
              const charProps = getCharCountProps(editingContent.length, MEMORY_CHAR_LIMIT);
              return (
                <Text size="xxs" color={charProps.color} style={charProps.style}>
                  {editingContent.length} / {MEMORY_CHAR_LIMIT}
                </Text>
              );
            })()}
          </View>
          <View style={styles.editActions}>
            <Button
              text={t('common.cancel')}
              variant="transparent"
              size="medium"
              isDisabled={isSaving}
              onPress={handleCancelEditing}
            />
            <Button
              text={t('settings.memory.saveChanges')}
              variant="primary"
              size="medium"
              isDisabled={!editingContent.trim() || editingContent.length > MEMORY_CHAR_LIMIT}
              isLoading={isSaving}
              onPress={handleUpdateMemory}
              iconColor={memColors.onInk}
              textStyle={{ color: memColors.onInk, fontWeight: '600' }}
              hoverBackgroundColor={memColors.ink}
              style={{
                backgroundColor: memColors.ink,
                borderRadius: 10,
                opacity: editingContent.trim() && editingContent.length <= MEMORY_CHAR_LIMIT ? 1 : 0.38,
              }}
            />
          </View>
        </View>
      ) : (
        <>
          <View style={[styles.memoryItemContent, isXS && !showCheckbox && { flexWrap: 'wrap' }]}>
            {showCheckbox && (
              <Pressable
                style={[styles.checkboxContainer, isSelected && styles.checkboxSelected]}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  handleCheckboxPress();
                }}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: isSelected }}
                {...(isWeb ? { 'aria-checked': isSelected } : {})}
                accessibilityLabel={`${t('common.select')}: ${memory.content}`}
              >
                {isSelected ? <Icon name="check" size={12} color={memColors.onInk} /> : null}
              </Pressable>
            )}

            <Text size="n" color="primary" style={[styles.memoryText, isXS && !showCheckbox && { flexBasis: '100%' }]}>
              {memory.content}
            </Text>

            {!showCheckbox && (
              <View
                style={[
                  styles.memoryActions,
                  (Platform.OS !== 'web' || isXS) && styles.memoryActionsVisible,
                ]}
                {...(isWeb
                  ? { dataSet: { memoryActions: isXS ? 'visible' : 'hidden' } }
                  : null)}
              >
                {/* Copy action */}
                <Tooltip label={isCopied ? t('pages.memory.copied') : t('pages.memory.copyMemory')} showIsXs>
                  <Pressable
                    style={({ hovered }) => [
                      styles.actionButton,
                      hovered && styles.actionButtonHovered,
                    ]}
                    onPress={handleCopy}
                    accessibilityRole="button"
                    accessibilityLabel={t('pages.memory.copyMemory')}
                  >
                    <Icon
                      name={isCopied ? 'check' : 'copy'}
                      size={13}
                      color={isCopied ? memColors.success : colors.text_color_secondary}
                    />
                  </Pressable>
                </Tooltip>

                {/* Edit action */}
                <Tooltip label={t('settings.memory.editMemory')} showIsXs>
                  <Pressable
                    style={({ hovered }) => [
                      styles.actionButton,
                      hovered && styles.actionButtonHovered,
                    ]}
                    onPress={handleStartEditing}
                    accessibilityRole="button"
                    accessibilityLabel={t('settings.memory.editMemory')}
                  >
                    <Icon name="pen" size={13} color={colors.text_color_secondary} />
                  </Pressable>
                </Tooltip>

                {/* Delete action */}
                <Tooltip label={t('settings.memory.deleteMemory')} showIsXs>
                  <Pressable
                    style={({ hovered }) => [
                      styles.actionButton,
                      styles.actionButtonDelete,
                      hovered && styles.actionButtonDeleteHovered,
                    ]}
                    onPress={handleDeletePress}
                    accessibilityRole="button"
                    accessibilityLabel={t('settings.memory.deleteMemory')}
                  >
                    <Icon name="trash" size={13} color={colors.text_color_secondary} />
                  </Pressable>
                </Tooltip>
              </View>
            )}
          </View>

          {/* Meta row: Provenance badge + Date + Conversation link */}
          <View style={styles.memoryMeta}>
            <View
              style={[
                styles.provenanceBadge,
                {
                  backgroundColor: provenance.bg,
                  borderColor: provenance.border,
                },
              ]}
            >
              <Icon
                name={provenance.icon}
                size={10}
                color={provenance.color}
              />
              <Text
                size="xxs"
                weight="semibold"
                style={{ color: provenance.color }}
              >
                {provenanceLabel}
              </Text>
            </View>

            <Text size="xs" color="secondary">
              {formatDate ? formatDate(memory.createdAt) : ''}
            </Text>

            {memory.fromConversation && (
              <Tooltip label={t('pages.memory.fromConversation')} showIsXs>
                <Pressable
                  style={({ hovered }) => [
                    styles.sourceLink,
                    hovered && styles.sourceLinkHovered,
                  ]}
                  onPress={handleOpenSource}
                  accessibilityRole="button"
                  accessibilityLabel={t('pages.memory.fromConversation')}
                >
                  <Icon
                    name="arrow-up-right-from-square"
                    size={10}
                    color={colors.text_color_secondary}
                  />
                  <Text size="xxs" color="secondary" weight="medium">
                    {t('pages.memory.fromConversation')}
                  </Text>
                </Pressable>
              </Tooltip>
            )}
          </View>
        </>
      )}
    </Pressable>
  );
};

export default React.memo(MemoryItem);
